/**
 * Users API endpoints
 * 
 * All user-related API calls go through this module.
 * 
 * Constitution Section 19.4: API Response Validation (Zod Mandatory)
 */

import { apiClient } from '../client';
import { z } from 'zod';

/**
 * User response schema
 */
const UserSchema = z.object({
  id: z.number(),
  email: z.string().email(),
  full_name: z.string().optional(),
  auth0_sub: z.string().optional(),
  personal_company: z.object({
    id: z.number(),
    name: z.string(),
  }).optional().nullable(),
}).passthrough();

export type User = z.infer<typeof UserSchema>;

/**
 * Get current user profile
 * 
 * Uses /users/me endpoint.
 * Validates response with Zod schema.
 */
export async function getCurrentUser(): Promise<User> {
  const response = await apiClient.get<unknown>('/users/me');
  
  // Zod parse at API boundary (Section 19.4)
  const user = UserSchema.parse(response.data);
  
  return user;
}

/**
 * Get user role
 */
export async function getUserRole(): Promise<string | null> {
  const response = await apiClient.get<unknown>('/users/me/role');
  
  if (response.data && typeof response.data === 'object' && 'role' in response.data) {
    return typeof response.data.role === 'string' ? response.data.role : null;
  }
  
  return null;
}
